import { Button } from "@dmca/frontend/components/button";
import {
  CellFeaturesEnum,
  type ICellHelpStatusNotification,
} from "@dmca/models/interface/cell-location";
import { Box, Stack, Typography } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import {
  CallHelpNotificationRow,
  CallHelpNotificationMutateModal,
  type CallHelpNotificationMutateModalProps,
} from "@dmca/frontend/widgets/admin/notification/call-help";
import { toast } from "react-toastify";
import type { inferInput } from "@trpc/tanstack-react-query";

export const Route = createFileRoute(
  "/_authed/_drawerLayout/admin/notification/call-help"
)({
  component: RouteComponent,
  loader: async ({ context: { trpcQcProxy: trpc, qc } }) => {
    qc.prefetchQuery(trpc.plant.getAll.queryOptions());
    const [cells, notifications, users] = await Promise.all([
      qc.ensureQueryData(trpc.cell.getAll.queryOptions()),
      qc.ensureQueryData(trpc.cell.getHelpStatusNotification.queryOptions()),
      qc.ensureQueryData(trpc.user.getAll.queryOptions()),
    ]);
    return {
      cells,
      notifications,
      users,
    };
  },
});

type DeviceIdSelection = { id: string; type: "plant" | "cell" | "all" };

function RouteComponent() {
  const { trpcQcProxy: trpc, qc } = Route.useRouteContext();
  const {
    cells: initialCells,
    notifications: initialNotifications,
    users: initialUsers,
  } = Route.useLoaderData();
  const [selectedCellIds, setSelectedCellIds] = useState<DeviceIdSelection[]>(
    []
  );
  const [cellIdsForCreation, setCellIdsForCreation] = useState<
    DeviceIdSelection[]
  >([]);
  const [isMultiSelect, setIsMultiSelect] = useState(false);

  const [selectedNotificationsForEdit, setSelectedNotificationsForEdit] =
    useState<ICellHelpStatusNotification[]>([]);
  const [openMutationModal, setOpenMutationModal] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);

  const { data: cells } = useQuery(
    trpc.cell.getAll.queryOptions(undefined, {
      select: (data) =>
        data
          .filter((x) => x.featureFlag.includes(CellFeaturesEnum.CALL_HELP))
          .sort((a, b) => a.locationName.localeCompare(b.locationName)),
      initialData: initialCells,
    })
  );

  const { data: plants } = useQuery(
    trpc.plant.getAll.queryOptions(undefined, {
      select: (data) => data.sort((a, b) => a.name.localeCompare(b.name)),
    })
  );

  const { data: users } = useQuery(
    trpc.user.getAll.queryOptions(undefined, {
      select: (data) => data.sort((a, b) => a.name.localeCompare(b.name)),
      initialData: initialUsers,
    })
  );

  const { data: notifications } = useQuery(
    trpc.cell.getHelpStatusNotification.queryOptions(undefined, {
      initialData: initialNotifications,
    })
  );

  const addMutation = useMutation(
    trpc.cell.addHelpStatusNotification.mutationOptions({
      onSuccess: () => {
        resetPage();
      },
    })
  );

  const resetPage = () => {
    setSelectedNotificationsForEdit([]);
    setCellIdsForCreation([]);
    setOpenMutationModal(false);
    setOpenCreateModal(false);
    setIsMultiSelect(false);
    qc.invalidateQueries(trpc.cell.getHelpStatusNotification.queryFilter());
  };

  const deleteMutation = useMutation(
    trpc.cell.deleteHelpStatusNotification.mutationOptions({
      onMutate: async (vars) => {
        await qc.cancelQueries(
          trpc.cell.getHelpStatusNotification.queryFilter()
        );
        qc.setQueryData(
          trpc.cell.getHelpStatusNotification.queryKey(),
          (oldData) =>
            oldData?.filter((x) => !vars.some((n) => n.id === x.id)) || []
        );
      },
      onSettled: () => {
        qc.invalidateQueries(trpc.iot.getAllNotificationContacts.queryFilter());
      },
      onSuccess: () => {
        resetPage();
      },
    })
  );

  const updateMutation = useMutation(
    trpc.cell.updateHelpStatusNotification.mutationOptions({
      onSuccess: () => {
        resetPage();
      },
    })
  );

  const onMutationModalSubmit: (
    type: "add" | "update"
  ) => CallHelpNotificationMutateModalProps["onSubmit"] =
    (type) => async (states, data) => {
      const { add, delete: toDelete, update } = data;
      const cellIdsToEdit =
        type === "add"
          ? cellIdsForCreation
          : selectedNotificationsForEdit.reduce((acc, item) => {
              if (item.cellId && acc.every((x) => x.id !== item.cellId)) {
                acc.push({ id: item.cellId, type: "cell" });
              } else if (
                item.plantId &&
                acc.every((x) => x.id !== item.plantId)
              ) {
                acc.push({ id: item.plantId, type: "plant" });
              } else if (
                item.allDevices &&
                acc.every((x) => x.type !== "all")
              ) {
                acc.push({ id: "", type: "all" });
              }
              return acc;
            }, [] as DeviceIdSelection[]);

      const promises: Promise<any>[] = [];

      if (toDelete.length > 0) {
        promises.push(
          deleteMutation.mutateAsync(
            toDelete.map((x) => ({
              id: x.id,
            }))
          )
        );
      }

      if (add.length > 0) {
        const notificationsToAdd: inferInput<
          typeof trpc.cell.addHelpStatusNotification
        > = states.flatMap((state) =>
          cellIdsToEdit.flatMap((cellId) =>
            add.map<inferInput<typeof trpc.cell.addHelpStatusNotification>[0]>(
              (item) => ({
                state,
                contact: item.contact,
                cell: cellId,
              })
            )
          )
        );
        promises.push(addMutation.mutateAsync(notificationsToAdd));
      }

      if (update.length > 0) {
        const notificationsToUpdate: inferInput<
          typeof trpc.cell.updateHelpStatusNotification
        > = update.map((item) => ({
          id: item.id,
          contact: item.contact,
        }));
        promises.push(updateMutation.mutateAsync(notificationsToUpdate));
      }

      const resp = await Promise.allSettled(promises);

      if (resp.some((x) => x.status === "rejected")) {
        console.error("Error occurred while submitting mutation modal.", resp);
        toast.error("An error occurred while processing your request.");
      } else {
        toast.success(
          `Notification contacts ${
            type === "add" ? "added" : "updated"
          } successfully.`
        );
      }
    };

  return (
    <>
      <Stack
        sx={{
          flexDirection: "column",
          width: "max-content",
        }}
      >
        <Stack direction="row" spacing={2}>
          <Button
            startIcon={isMultiSelect ? undefined : <CheckBoxOutlinedIcon />}
            size="small"
            variant="text"
            color={isMultiSelect ? "error" : "primary"}
            onClick={() => {
              setCellIdsForCreation([]);
              setIsMultiSelect((prev) => !prev);
            }}
            sx={{
              p: 1,
              m: 0,
            }}
          >
            {isMultiSelect ? "Cancel" : "Select Multiple"}
          </Button>
          {!isMultiSelect && (
            <Button
              startIcon={
                selectedCellIds.length ===
                (cells ?? []).length + (plants ?? []).length + 1 ? ( // all devices is one
                  (<ExpandLess />)
                ) : (
                  <ExpandMore />
                )
              }
              size="small"
              variant="text"
              sx={{ p: 1, m: 0 }}
              onClick={() => {
                setSelectedCellIds((prev) =>
                  prev.length ===
                  (cells ?? []).length + (plants ?? []).length + 1 // all devices is one
                    ? []
                    : [
                        ...(cells ?? []).map((cell) => ({
                          id: cell.cellId,
                          type: "cell" as const,
                        })),
                        ...(plants ?? []).map((plant) => ({
                          id: plant.id,
                          type: "plant" as const,
                        })),
                        {
                          type: "all" as const,
                          id: "",
                        },
                      ]
                );
              }}
            >
              {selectedCellIds.length !==
              (cells ?? []).length + (plants ?? []).length + 1 // all devices is one
                ? "Expand"
                : "Collapse"}{" "}
              All
            </Button>
          )}
          {isMultiSelect && cellIdsForCreation.length > 0 && (
            <Button
              size="small"
              variant="text"
              sx={{ p: 1, m: 0 }}
              onClick={() => {
                setOpenCreateModal(true);
              }}
            >
              Add Notifications
            </Button>
          )}
        </Stack>
        <CallHelpNotificationRow
          label="All Cells"
          showCheckbox={isMultiSelect}
          checkboxChecked={cellIdsForCreation.some(
            (item) => item.type === "all"
          )}
          onCheckboxChange={(checked) => {
            setCellIdsForCreation((prev) => {
              if (checked) {
                return [...prev, { type: "all" as const, id: "" }];
              } else {
                return prev.filter((item) => item.type !== "all");
              }
            });
          }}
          onAddButtonClick={() => {
            setCellIdsForCreation([{ id: "", type: "all" }]);
            setOpenCreateModal(true);
          }}
          onEditButtonClick={(notifications) => {
            setSelectedNotificationsForEdit(notifications);
            setOpenMutationModal(true);
          }}
          onDeleteButtonClick={(notifications) => {
            deleteMutation.mutate(
              notifications.map((x) => ({
                id: x.id,
              }))
            );
          }}
          notifications={(notifications ?? []).filter((x) => x.allDevices)}
          expanded={
            !isMultiSelect &&
            selectedCellIds.some((item) => item.type === "all")
          }
          users={users ?? []}
          onCollapseButtonClick={() => {
            setSelectedCellIds((prev) =>
              prev.some((item) => item.type === "all")
                ? prev.filter((item) => item.type !== "all")
                : [
                    ...prev,
                    {
                      id: "",
                      type: "all" as const,
                    },
                  ]
            );
          }}
        />
        {plants?.map((plant) => (
          <Box key={plant.id} data-testid="call-help-plant-row">
            <Typography fontWeight={600}>{plant.name}</Typography>
            <CallHelpNotificationRow
              label={plant.name}
              showCheckbox={isMultiSelect}
              checkboxChecked={cellIdsForCreation.some(
                (item) => item.type === "plant" && item.id === plant.id
              )}
              onCheckboxChange={(checked) => {
                setCellIdsForCreation((prev) => {
                  if (checked) {
                    return [...prev, { type: "plant" as const, id: plant.id }];
                  } else {
                    return prev.filter(
                      (item) => item.type !== "plant" || item.id !== plant.id
                    );
                  }
                });
              }}
              onAddButtonClick={() => {
                setCellIdsForCreation([{ id: plant.id, type: "plant" }]);
                setOpenCreateModal(true);
              }}
              onEditButtonClick={(notifications) => {
                setSelectedNotificationsForEdit(notifications);
                setOpenMutationModal(true);
              }}
              onDeleteButtonClick={(notifications) => {
                deleteMutation.mutate(
                  notifications.map((x) => ({
                    id: x.id,
                  }))
                );
              }}
              notifications={(notifications ?? []).filter(
                (x) => x.plantId === plant.id
              )}
              expanded={
                !isMultiSelect &&
                selectedCellIds.some(
                  (item) => item.type === "plant" && item.id === plant.id
                )
              }
              users={users ?? []}
              onCollapseButtonClick={() => {
                setSelectedCellIds((prev) =>
                  prev.some(
                    (item) => item.type === "plant" && item.id === plant.id
                  )
                    ? prev.filter(
                        (item) => item.type !== "plant" || item.id !== plant.id
                      )
                    : [
                        ...prev,
                        {
                          id: plant.id,
                          type: "plant" as const,
                        },
                      ]
                );
              }}
            />
            {cells
              ?.filter((cell) => cell.plantId === plant.id)
              .sort((x, y) => x.locationName.localeCompare(y.locationName))
              .map((cell) => (
                <CallHelpNotificationRow
                  key={cell.cellId}
                  data-testid="call-help-cell-row"
                  showCheckbox={isMultiSelect}
                  onCheckboxChange={(checked) => {
                    setCellIdsForCreation((prev) => {
                      if (checked) {
                        return [...prev, { id: cell.cellId, type: "cell" }];
                      } else {
                        return prev.filter(
                          (item) =>
                            item.type !== "cell" || item.id !== cell.cellId
                        );
                      }
                    });
                  }}
                  checkboxChecked={cellIdsForCreation.some(
                    (item) => item.type === "cell" && item.id === cell.cellId
                  )}
                  expanded={
                    !isMultiSelect &&
                    selectedCellIds.some(
                      (item) => item.type === "cell" && item.id === cell.cellId
                    )
                  }
                  onCollapseButtonClick={() => {
                    setSelectedCellIds((prev) =>
                      prev.some(
                        (item) =>
                          item.type === "cell" && item.id === cell.cellId
                      )
                        ? prev.filter(
                            (item) =>
                              item.type !== "cell" || item.id !== cell.cellId
                          )
                        : [
                            ...prev,
                            {
                              id: cell.cellId,
                              type: "cell" as const,
                            },
                          ]
                    );
                  }}
                  label={cell.locationName}
                  onAddButtonClick={() => {
                    setCellIdsForCreation([{ id: cell.cellId, type: "cell" }]);
                    setOpenCreateModal(true);
                  }}
                  onEditButtonClick={(notifications) => {
                    setSelectedNotificationsForEdit(notifications);
                    setOpenMutationModal(true);
                  }}
                  onDeleteButtonClick={(notifications) => {
                    deleteMutation.mutate(
                      notifications.map((x) => ({
                        id: x.id,
                      }))
                    );
                  }}
                  notifications={(notifications ?? []).filter(
                    (x) => x.cellId === cell.cellId
                  )}
                  users={users ?? []}
                />
              ))}
          </Box>
        ))}
      </Stack>
      {openMutationModal && (
        <CallHelpNotificationMutateModal
          open={openMutationModal}
          onClose={() => {
            setOpenMutationModal(false);
          }}
          type="update"
          header={`Update Call Help Notifications for ${
            selectedNotificationsForEdit[0]?.cellId
              ? cells?.find(
                  (x) => x.cellId === selectedNotificationsForEdit[0].cellId
                )?.locationName
              : selectedNotificationsForEdit[0]?.plantId
              ? plants?.find(
                  (x) => x.id === selectedNotificationsForEdit[0].plantId
                )?.name
              : "All Cells"
          }`}
          onSubmit={onMutationModalSubmit("update")}
          notifications={selectedNotificationsForEdit}
          users={users ?? []}
          isSubmitting={
            addMutation.isPending ||
            updateMutation.isPending ||
            deleteMutation.isPending
          }
        />
      )}
      {openCreateModal && (
        <CallHelpNotificationMutateModal
          open={openCreateModal}
          header={`Create Call Help Notification for ${
            !isMultiSelect
              ? cellIdsForCreation[0]?.type === "cell"
                ? cells?.find((x) => x.cellId === cellIdsForCreation[0].id)
                    ?.locationName
                : cellIdsForCreation[0]?.type === "all"
                ? "All Cells"
                : plants?.find((x) => x.id === cellIdsForCreation[0]?.id)?.name
              : "Multiple Cells"
          }`}
          onClose={() => {
            setOpenCreateModal(false);
          }}
          type="add"
          onSubmit={onMutationModalSubmit("add")}
          isSubmitting={
            addMutation.isPending ||
            updateMutation.isPending ||
            deleteMutation.isPending
          }
          users={users ?? []}
        />
      )}
    </>
  )
}
